import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SITE } from "@/lib/site";
import DisclaimerBanner from "@/components/DisclaimerBanner";

const PILLARS = [
  {
    title: "Top-down & bottom-up",
    body:
      "How the brain and body each shape your stress response — and why working with both is more effective than working with either alone.",
  },
  {
    title: "The vagus nerve",
    body:
      "An evidence-based look at the science of nervous-system regulation, and the practical levers you actually have over your physiology.",
  },
  {
    title: "Overstimulation",
    body:
      "How to recognise an overstimulated nervous system in modern life, and what to do that fits a real schedule.",
  },
  {
    title: "Saying no, and being okay",
    body:
      "A working framework for declining what depletes you, in language that fits real relationships.",
  },
  {
    title: "Trigger identification",
    body:
      "Trace patterns back to their sources with clarity — separating the situation, the sensation, and the story.",
  },
  {
    title: "Daily self-reflection",
    body:
      "A short, sustainable practice for noticing what your nervous system is asking for.",
  },
];

export default function Home() {
  return (
    <div className="rr-reveal">
      {/* Hero — typography first, no decorative boxes */}
      <section className="pt-24 md:pt-36 pb-24 md:pb-32" data-testid="hero-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12">
          <p
            className="text-xs tracking-[0.22em] uppercase mb-8 font-medium"
            style={{ color: "var(--rr-primary)" }}
            data-testid="hero-overline"
          >
            A six-week live online course
          </p>
          <h1
            className="font-serif text-[44px] sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight"
            style={{ color: "var(--rr-text)" }}
            data-testid="hero-title"
          >
            Understanding your nervous system in a stress-filled world.
          </h1>
          <p
            className="mt-8 text-lg sm:text-xl leading-relaxed max-w-2xl"
            style={{ color: "var(--rr-text-2)" }}
            data-testid="hero-subtitle"
          >
            An evidence-based, six-week educational program — taught by a board-certified psychotherapist with a master’s degree in trauma counseling. Practical instruction and information for the nervous system you actually live in.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/enroll"
              data-testid="hero-enroll-button"
              className="inline-flex items-center gap-3 px-8 py-4 text-sm font-medium transition-colors group"
              style={{ background: "var(--rr-primary)", color: "var(--rr-bg)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--rr-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--rr-primary)")}
            >
              Enroll for {SITE.priceLabel}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/curriculum"
              data-testid="hero-curriculum-button"
              className="inline-flex items-center gap-3 px-8 py-4 text-sm font-medium rr-link"
              style={{ color: "var(--rr-text)" }}
            >
              See the curriculum
            </Link>
          </div>

          <div
            className="mt-20 pt-10 border-t grid grid-cols-2 sm:grid-cols-4 gap-8 text-sm"
            style={{ borderColor: "var(--rr-border)", color: "var(--rr-text-2)" }}
          >
            <div>
              <p className="text-xs tracking-[0.18em] uppercase mb-2" style={{ color: "var(--rr-primary)" }}>
                Begins
              </p>
              <p data-testid="hero-start-date">{SITE.course.startDate}</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] uppercase mb-2" style={{ color: "var(--rr-primary)" }}>
                Format
              </p>
              <p>{SITE.course.format}</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] uppercase mb-2" style={{ color: "var(--rr-primary)" }}>
                Duration
              </p>
              <p>{SITE.course.weeks} weeks</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] uppercase mb-2" style={{ color: "var(--rr-primary)" }}>
                Tuition
              </p>
              <p>{SITE.priceLabel} total</p>
            </div>
          </div>
        </div>
      </section>

      <DisclaimerBanner />

      {/* What you'll learn — clean grid, no week numbers, no card backgrounds */}
      <section className="py-24 md:py-32" data-testid="what-youll-learn-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="max-w-2xl">
            <p
              className="text-xs tracking-[0.22em] uppercase mb-6 font-medium"
              style={{ color: "var(--rr-primary)" }}
            >
              What you’ll learn
            </p>
            <h2
              className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.08] tracking-tight"
              style={{ color: "var(--rr-text)" }}
            >
              Six grounded ideas. One nervous system — yours.
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
            {PILLARS.map((p, i) => (
              <article key={i} data-testid={`pillar-0${i + 1}`}>
                <p
                  className="font-mono text-xs tracking-[0.18em]"
                  style={{ color: "var(--rr-accent)" }}
                >
                  0{i + 1}
                </p>
                <h3
                  className="mt-3 font-serif text-2xl leading-tight"
                  style={{ color: "var(--rr-text)" }}
                >
                  {p.title}
                </h3>
                <p
                  className="mt-4 text-base leading-relaxed"
                  style={{ color: "var(--rr-text-2)" }}
                >
                  {p.body}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-16">
            <Link
              to="/curriculum"
              className="rr-link text-sm"
              style={{ color: "var(--rr-primary)" }}
              data-testid="home-view-full-curriculum"
            >
              View the full curriculum →
            </Link>
          </div>
        </div>
      </section>

      {/* Instructor — quieter, single column with no overlay */}
      <section
        className="py-24 md:py-32 border-t"
        style={{ borderColor: "var(--rr-border)" }}
        data-testid="instructor-teaser"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12">
          <p
            className="text-xs tracking-[0.22em] uppercase mb-6 font-medium"
            style={{ color: "var(--rr-primary)" }}
          >
            Your instructor
          </p>
          <h2
            className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight"
            style={{ color: "var(--rr-text)" }}
          >
            {SITE.instructor.shortName}
          </h2>
          <p className="mt-3 text-base" style={{ color: "var(--rr-text-2)" }}>
            {SITE.instructor.credentials}
          </p>
          <p
            className="mt-8 text-lg leading-relaxed max-w-2xl"
            style={{ color: "var(--rr-text-2)" }}
          >
            A licensed board-certified psychotherapist. Rewire and Root distills what reliably helps her clients into language anyone can use — taught with the seriousness it deserves.
          </p>
          <Link
            to="/about"
            className="rr-link inline-block mt-8 text-sm"
            style={{ color: "var(--rr-primary)" }}
            data-testid="instructor-read-more"
          >
            Read more about Aimee →
          </Link>
        </div>
      </section>

      {/* Final CTA — quieter */}
      <section
        className="py-24 md:py-32"
        style={{ background: "var(--rr-surface)" }}
        data-testid="home-final-cta"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-12">
          <p
            className="text-xs tracking-[0.22em] uppercase mb-6 font-medium"
            style={{ color: "var(--rr-primary)" }}
            data-testid="home-cohort-banner"
          >
            {SITE.course.startDateLong}
          </p>
          <h2
            className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl"
            style={{ color: "var(--rr-text)" }}
          >
            Begin where you actually are.
          </h2>
          <p
            className="mt-6 text-lg leading-relaxed max-w-2xl"
            style={{ color: "var(--rr-text-2)" }}
          >
            Tuition is {SITE.priceLabel} for the full six-week program. One simple payment, one small group, one focused subject — your nervous system.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/enroll"
              data-testid="home-final-enroll-button"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-medium"
              style={{ background: "var(--rr-primary)", color: "var(--rr-bg)" }}
            >
              Enroll for {SITE.priceLabel}
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="home-final-contact-button"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-medium rr-link"
              style={{ color: "var(--rr-text)" }}
            >
              Ask a question
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
